'use client';

import React, { useEffect, useMemo, useState } from 'react';

import Checkmark from 'public/assets/icons/checkmark.svg';
import ExMark from 'public/assets/icons/exclamation-mark.svg';
import Price from '@/components/general/Price';
import { Auction, Item, ItemImage, MessagingSubscriptionCodeResponseDto, OfferTransaction } from '@/api';
import useAuction from '@/hooks/useAuction';
import { useQuery } from '@tanstack/react-query';
import { getUserPaymentMethodsFn } from '@/queries/payment';
import useAuth from '@/contexts/AuthContext';
import clsx from 'clsx';
import { getMessagingSubscriberCodeQueryFn, getUserCreditLimitFn } from '@/queries/account';
import { usePathname } from 'next/navigation';
import i18next from 'i18n';
import Sheet from 'react-modal-sheet';
import { useMobile } from '@/hooks/useMobile';
import dayjs from 'dayjs';
import { ProductPageGallery, ProductPageGalleryItem } from '@/components/general/ProductPageGallery';
import { useHasWindow } from '@/hooks/useHasWindow';
import useOffers from '@/hooks/useOffers';
import PBBuyNowButton from '@/components/ecommerce/product/ProductBid/Components/PBBuyNowButton';
import PBCodeAndBadge from '@/components/ecommerce/product/ProductBid/Components/PBCodeAndBadge';
import PBProductTitle from '@/components/ecommerce/product/ProductBid/Components/PBProductTitle';
import PBBidDetails from '@/components/ecommerce/product/ProductBid/Components/PBBidDetails';
import PBBidStatus from '@/components/ecommerce/product/ProductBid/Components/PBBidStatus';
import PBCongrats from '@/components/ecommerce/product/ProductBid/Components/PBCongrats';
import PBButtons from '@/components/ecommerce/product/ProductBid/Components/PBButtons';
import AuctionPricePromotion from '@/contexts/AuctionPricePromotion';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useFavorite } from '@/hooks/api/useFavorite';
import Image from 'next/image';
import ProductBreadCrumbs from '../ProductBreadCrumbs';
import ShareModal from '../../ShareModal';
import Share from '../../Share';
import ProductBidModal from './ProductBidModal';
import ProductOfferModal from './ProductOfferModal';
import PBPutOffer from './Components/PBPutOffer';
import PBVariants from './Components/PBVariants';

export type BidLeadingState = 'winning' | 'loosing' | 'won' | undefined;

interface ProductBidProps {
    product?: Item,
    onImageClicked?: (index: number) => void;
    modal?: boolean,
    bottomSheet?: boolean,
    isLoading?: boolean;
    onBidStateChange?: (state: BidLeadingState) => void
}

const ProductBid = ({ product, onImageClicked, modal, isLoading, bottomSheet, onBidStateChange }: ProductBidProps) => {
    const [showShare, setShowShare] = useState<boolean>(false);
    const [showBidding, setShowBidding] = useState<boolean>(false);
    const [showOffering, setShowOffering] = useState<boolean>(false);
    const [showAuctionPhaseInfo, setShowAuctionPhaseInfo] = useState<boolean>(false);
    const isMobile = useMobile();
    const hasWindow = useHasWindow();
    const { user } = useAuth();

    const { isFavorite } = useFavorite();

    const { hasAuction, auction, loading, hasLostAuction, auctionOver, lastBid, isWinning, endTime, hasUserAuctionAccess, hasWonAuction, isOwner, isLoosing, pickupOption, comingSoon } = useAuction(product, undefined, true);

    const pathname = usePathname();
    const { offerPossible, currentOffer, offerIsLoading, offerPossibleIsLoading } = useOffers(product, true);

    const { data: accounts } = useQuery({
        queryFn: getUserPaymentMethodsFn,
        queryKey: ['getUserPaymentMethodsFn'],
        enabled: !!user
    });

    const { data: creditLimit } = useQuery({
        queryKey: ['getUserCreditLimitFn', auction?.id],
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        queryFn: () => getUserCreditLimitFn(auction!.id),
        enabled: !!auction && hasUserAuctionAccess
    });

    const { data: subscriberCode } = useQuery({
        refetchOnWindowFocus: true,
        queryKey: ['getMessagingSubscriberCodeQueryFn'],
        queryFn: getMessagingSubscriberCodeQueryFn,
        enabled: !!(auction && hasUserAuctionAccess && auction.status !== Auction.StatusEnum.Closed && auction.status !== Auction.StatusEnum.Done && auction.type === Auction.TypeEnum.Auction)
    });

    useEffect(() => {
        if (auction?.status === Auction.StatusEnum.Blind) {
            onBidStateChange?.(undefined);
            return;
        }
        if (isWinning) {
            if (auction?.status === Auction.StatusEnum.Done) {
                if (dayjs().isBefore(dayjs(auction.end_date).add(1, 'week'))) {
                    onBidStateChange?.('won');
                }
            } else {
                onBidStateChange?.('winning');
            }
        } else if (lastBid) {
            onBidStateChange?.('loosing');
        } else {
            onBidStateChange?.(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isWinning, lastBid, auction?.status, onBidStateChange]);

    const isEverythingLoaded = useMemo(() => !isLoading && (user ? !offerIsLoading && !offerPossibleIsLoading : true), [isLoading, offerIsLoading, offerPossibleIsLoading, user]);

    const hasAuctionFlag = hasAuction && isEverythingLoaded && !offerPossible;
    const showGreyBox = (hasAuctionFlag || comingSoon || product?.current_offer_transaction) && isEverythingLoaded;

    return (
        <div className="px-[18px]">
            <ProductBreadCrumbs className="hidden sm-tablet:hidden md:flex" item={product} isLoading={!isEverythingLoaded} />
            <div className="flex flex-col w-[full] @container">
                <div className="mx-[24px] md:hidden sm-tablet:block">
                    <ProductBreadCrumbs item={product} isLoading={!isEverythingLoaded} />
                </div>
                <div className={clsx('flex',
                    'flex-col-reverse sm-tablet:flex-col-reverse md:flex-row-reverse',
                    'items-start sm-tablet:justify-center justify-between')}>
                    <div className="flex-col flex justify-center  max-w-full  w-full sm-tablet:!max-w-full  tablet:!max-w-[480px]  md:!max-w-[480px] mt-[40px] lg:mt-[0]">

                        <PBCodeAndBadge isEverythingLoaded={isEverythingLoaded} modal={modal} product={product} />
                        <PBProductTitle isEverythingLoaded={isEverythingLoaded} modal={modal} product={product} pickupOption={hasAuctionFlag && isEverythingLoaded ? pickupOption : undefined} />

                        <PBVariants
                            product={product}
                            isOwner={isOwner} />

                        {showGreyBox &&
                            <div className="md:max-w-[480px] md:mx-0 mx-auto w-full sm-tablet:max-w-[unset] bg-grey16 rounded-[10px] p-[20px] mb-[40px]">
                                {!product?.current_offer_transaction && <PBCongrats
                                    auction={auction}
                                    isWinning={isWinning}
                                    user={user}
                                    product={product}
                                    isLoosing={isLoosing}
                                    hasUserAuctionAccess={hasUserAuctionAccess}
                                    hasLostAuction={hasLostAuction} />}

                                <PBBidStatus
                                    setShowAuctionPhaseInfo={(val: boolean) => setShowAuctionPhaseInfo(val)}
                                    hasAuction={hasAuction}
                                    isLoosing={isLoosing}
                                    lastBid={lastBid}
                                    endTime={endTime}
                                    showAuctionPhaseInfo={showAuctionPhaseInfo}
                                    isWinning={isWinning}
                                    auction={auction}
                                    comingSoon={comingSoon} />

                                {hasAuctionFlag && <PBBidDetails hasAuction={hasAuction} auctionOver={auctionOver} endTime={endTime} auction={auction} />}
                                {hasAuctionFlag && auction?.type === Auction.TypeEnum.BuyNow && (
                                    auctionOver ?
                                        <div>
                                            <AuctionPricePromotion auction={auction}>{({ isPromoted, amount, promotedAmount }) => {
                                                return (
                                                    <div>{i18next.t('common:item-text.sold-out')}
                                                        <Price className={clsx('@[600px]:text-[2rem] text-[1rem] font-[700]', { 'line-through @[600px]:text-[1rem] text-red text-sm': isPromoted })} justification="L" number={amount} />
                                                        {isPromoted && <Price className={clsx('@[600px]:text-[2rem] text-[1.4rem] font-[700]')} justification="L" number={promotedAmount} />}
                                                    </div>
                                                );
                                            }}
                                            </AuctionPricePromotion>
                                        </div>
                                        :
                                        <div>
                                            <AuctionPricePromotion auction={auction}>{({ isPromoted, amount, promotedAmount }) => {
                                                return (
                                                    <div>{i18next.t('common:item-text.price')}
                                                        <Price className={clsx('@[600px]:text-[2rem] text-[1rem] font-[700]', { 'line-through @[600px]:text-[1rem] text-red text-sm': isPromoted })} justification="L" number={amount} />
                                                        {isPromoted && <Price className={clsx('@[600px]:text-[2rem] text-[1.4rem] font-[700]')} justification="L" number={promotedAmount} />}
                                                    </div>
                                                );
                                            }}
                                            </AuctionPricePromotion>
                                        </div>
                                )}
                                {!hasAuctionFlag && product?.current_offer_transaction && (
                                    product?.owner_id !== user?.id ?
                                        <div>
                                            <div>{i18next.t('common:item-text.bought-for')}<Price className="@[600px]:text-[2rem] text-[1rem] font-[700]" justification="L" number={product?.current_offer_transaction.current_amount ?? '0'} /></div>
                                        </div>
                                        :
                                        <div className="flex justify-between">
                                            <div>{i18next.t('common:item-text.sold-for')}<Price className="@[600px]:text-[2rem] text-[1rem] font-[700]" justification="L" number={product?.current_offer_transaction.current_amount ?? '0'} /></div>
                                            <div>{i18next.t('common:offers.payout')}<Price className="@[600px]:text-[2rem] text-[1rem] font-[700]" justification="L" number={product?.current_offer_transaction.payout_amount ?? '0'} /></div>
                                        </div>
                                )}
                                <AnimatePresence>
                                    {subscriberCode &&
                                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center text-[14px] mt-[12px] leading-[14px]">
                                            <span className="text-[14px] font-[500] text-grey15 leading-[1.2rem]">
                                                {subscriberCode.status === MessagingSubscriptionCodeResponseDto.StatusEnum.Pending && subscriberCode.code ?
                                                    <>
                                                        {i18next.t('common:sms-subscription.send-code-to-susbscribe', {
                                                            code: subscriberCode.code,
                                                            subscribe_phone_number: parsePhoneNumberFromString(subscriberCode.subscribe_phone_number)?.formatNational({})
                                                        })} {' '}
                                                        <Link href="/terms-of-service#sms" target="_blank" className="underline">{i18next.t('common:sms-subscription.terms')}</Link>
                                                    </> : null}
                                                {subscriberCode.status === MessagingSubscriptionCodeResponseDto.StatusEnum.Subscribed && (
                                                    !lastBid && !isFavorite(product?.id) ? i18next.t('common:sms-subscription.add-to-favorites-reminder') : i18next.t('common:sms-subscription.already-subscribed')
                                                )}
                                                {subscriberCode.status === MessagingSubscriptionCodeResponseDto.StatusEnum.UnSubscribed ?
                                                    <>
                                                        {i18next.t('common:sms-subscription.send-start-to-susbscribe', {
                                                            subscribe_phone_number: parsePhoneNumberFromString(subscriberCode.subscribe_phone_number)?.formatNational({})
                                                        })} {' '}
                                                        <Link href="/terms-of-service#sms" target="_blank" className="underline">{i18next.t('common:sms-subscription.terms')}</Link>
                                                    </> : null}
                                            </span>
                                        </motion.div>
                                    }
                                </AnimatePresence>
                            </div>
                        }

                        {product?.current_offer_transaction && product?.current_offer_transaction.status === OfferTransaction.StatusEnum.Pending && product?.owner_id !== user?.id &&
                            <div className="flex items-center text-[14px] leading-[14px] py-[10px] px-2 justify-center  mb-4 bg-green4 text-green5 rounded-[8px]">
                                <span className="w-[24px] font-[500] mr-[5px]"><Checkmark className="text-green5" /></span> {i18next.t('common:offers.congrats-purchased')}
                            </div>
                        }

                        {product?.current_offer_transaction && product?.current_offer_transaction.status === OfferTransaction.StatusEnum.Paid && product?.owner_id === user?.id &&
                            <div className="flex items-center text-[14px] leading-[14px] py-[18px] px-4 justify-center  mb-4 bg-grey16 text-black rounded-[8px]">
                                <span className="w-[24px] font-[500] mr-[5px]"><ExMark className="text-black" /></span> {i18next.t('common:offers.in-transaction-print-label')}
                            </div>
                        }

                        {product?.current_offer_transaction && product?.current_offer_transaction.status === OfferTransaction.StatusEnum.Paid && product?.owner_id !== user?.id &&
                            <div className="flex items-center text-[14px] leading-[14px] py-[10px] px-2 justify-center  mb-4 bg-green4 text-green5 rounded-[8px]">
                                <span className="w-[24px] font-[500] mr-[5px]"><Checkmark className="text-green5" /></span> {i18next.t('common:offers.awaiting-shipping-seller')}
                            </div>
                        }

                        {product?.current_offer_transaction?.status === OfferTransaction.StatusEnum.OfficeReceived &&
                            <div className="flex items-center text-[14px] leading-[14px] py-[10px] px-2 justify-center  mb-4 bg-grey16 text-black rounded-[8px]">
                                <span className="w-[24px] font-[500] mr-[5px]"><ExMark className="text-black" /></span>      {i18next.t('common:offers.awaiting-reauthentication')}
                            </div>

                        }
                        {product?.current_offer_transaction?.status === OfferTransaction.StatusEnum.Reauthenticated &&
                            <div className="flex items-center text-[14px] leading-[14px] py-[10px] px-2 justify-center  mb-4 bg-green4 text-green5 rounded-[8px]">
                                <span className="w-[24px] font-[500] mr-[5px]"><Checkmark className="text-green5" /></span>   {i18next.t('common:offers.reauthenticated')}
                            </div>
                        }
                        {product?.current_offer_transaction?.status === OfferTransaction.StatusEnum.ReauthenticatedFailed &&
                            <div className="flex items-center text-[14px] leading-[14px] py-[10px] px-2 justify-center  mb-4 bg-grey16 text-red4 rounded-[8px]">
                                {i18next.t('common:offers.reauthenticated-failed')}
                            </div>
                        }
                        {product?.owner_id === user?.id && (product?.current_offer_transaction?.status === OfferTransaction.StatusEnum.ShippedToBuyer || product?.current_offer_transaction?.status === OfferTransaction.StatusEnum.Done) &&
                            <div className="flex items-center text-[14px] leading-[14px] py-[10px] px-2 justify-center  mb-4 bg-grey16 text-black rounded-[8px]">
                                {i18next.t('common:offers.transaction-completed')}
                            </div>
                        }
                        {product?.owner_id !== user?.id && (product?.current_offer_transaction?.status === OfferTransaction.StatusEnum.ShippedToBuyer) &&
                            <div className="flex items-center text-[14px] leading-[14px] py-[10px] px-2 justify-center  mb-4 bg-green4 text-green5 rounded-[8px]">
                                <span className="w-[24px] font-[500] mr-[5px]"><Checkmark className="text-green5" /></span>    {i18next.t('common:offers.shipped')}
                            </div>
                        }
                        {product?.owner_id !== user?.id && (product?.current_offer_transaction?.status === OfferTransaction.StatusEnum.Done) &&
                            <div className="flex items-center text-[14px] leading-[14px] py-[10px] px-2 justify-center  mb-4 bg-grey16 text-black rounded-[8px]">
                                <span className="w-[24px] font-[500] mr-[5px]"><Checkmark className="text-green5" /></span>  {i18next.t('common:offers.transaction-completed')}
                            </div>
                        }
                        {currentOffer?.is_accepted && product?.owner_id === user?.id && product?.current_offer_transaction?.status === OfferTransaction.StatusEnum.Pending &&
                            <div className="flex items-center text-[14px] leading-[14px] py-[10px] px-2 justify-center  mb-4 bg-green4 text-green5 rounded-[8px]">
                                <span className="w-[24px] font-[500] mr-[5px]"><Checkmark className="text-green5" /></span> {i18next.t('common:offers.congrats-sold')}
                            </div>
                        }

                        {product && <PBPutOffer offerPossible={offerPossible} currentOffer={currentOffer} isEverythingLoaded={isEverythingLoaded} product={product} user={user} onShowOffering={setShowOffering} />}

                        <PBBuyNowButton
                            auction={auction}
                            hasAuctionFlag={hasAuctionFlag}
                            user={user}
                            product={product}
                            hasWonAuction={hasWonAuction}
                            isOwner={isOwner} />

                        <PBButtons
                            auction={auction}
                            isEverythingLoaded={isEverythingLoaded}
                            user={user}
                            auctionOver={auctionOver}
                            pathname={pathname}
                            hasAuctionFlag={hasAuctionFlag}
                            loading={loading}
                            hasUserAuctionAccess={hasUserAuctionAccess}
                            hasWonAuction={hasWonAuction}
                            modal={modal}
                            comingSoon={!!comingSoon}
                            product={product}
                            showShare={() => setShowShare(true)}
                            showBidding={() => setShowBidding(true)}
                        />

                    </div>
                    <div className="flex items-center md:mr-[40px] mr-[0] w-full">

                        <div className=" bg-grey11 md:bg-white md:border-none px-4 md:px-0 border-b-[1px] mb-[24px] md:mb-0 w-full">
                            <div className={clsx(bottomSheet && 'w-[50%] mx-auto md:w-full')}>
                                <ProductPageGallery<ItemImage>
                                    items={isEverythingLoaded && product ? product.images : [{ id: 'item-1' }, { id: 'item-2' }, { id: 'item-3' }] as ItemImage[]}
                                    activeNavColor="#98A2B3"
                                    isLoading={!isEverythingLoaded}
                                    key={product?.id}
                                    renderItem={({
                                        item,
                                        index,
                                        isSnapPoint,
                                    }) => (
                                        <ProductPageGalleryItem
                                            className="cursor-nesw-resize"
                                            key={item.id}
                                            isSnapPoint={isSnapPoint}
                                            fullWidth
                                            onClick={() => onImageClicked && onImageClicked(index)}
                                        >
                                            {item.type === ItemImage.TypeEnum.Image &&
                                                <>
                                                    <img className="self-center absolute h-full z-0 inset-0 blur-3xl transform-cpu touch-none" style={{ transform: 'translate3d(0,0,0)' }} src={item.url} alt="" />
                                                    <img className="self-center z-10" src={item.url} alt="" />
                                                </>
                                            }
                                            {item.type === ItemImage.TypeEnum.Video && hasWindow && <video preload="auto" autoPlay playsInline controls={false} loop muted className="self-center" src={item.url} />}
                                        </ProductPageGalleryItem>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                    <ProductBidModal show={showBidding} onHide={() => setShowBidding(false)} accounts={accounts} product={product} creditLimit={creditLimit} allowVipBidding={user?.allow_vip_bidding} />
                    <ProductOfferModal offer={currentOffer} show={showOffering} onHide={() => setShowOffering(false)} product={product} />
                    <ShareModal product={product} show={showShare && !isMobile} onHide={() => setShowShare(false)} isLoading={!isEverythingLoaded} />
                    <Sheet isOpen={showShare && isMobile} onClose={() => setShowShare(false)} tweenConfig={{ ease: 'easeInOut', duration: 0.4 }}>
                        <Sheet.Container>
                            <Sheet.Header />
                            <Sheet.Content>
                                <Sheet.Scroller>
                                    {product && <Share isLoading={!isEverythingLoaded} product={product} isMobile={isMobile} />}
                                </Sheet.Scroller>
                            </Sheet.Content>
                        </Sheet.Container>
                        <Sheet.Backdrop onTap={() => setShowShare(false)} />
                    </Sheet>
                </div>

            </div>
        </div>

    );
};

export default ProductBid;
